import { Pressable, Text, View } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import type { CategoryGroup } from '../lib/category-config';
import { formatCurrency, formatPercent } from '../lib/utils';
import { useColors } from '../lib/colors';
import { Button } from './Button';
import { Card } from './Card';
import { CurrencyInput } from './CurrencyInput';
import { Money } from './Money';
import { ProgressBar } from './ProgressBar';
import { SectionHeader } from './SectionHeader';
import { webPrivacyProps } from '../lib/privacy';

export type BudgetCategoryLifeEvent = {
  goalId: string;
  description: string;
  allowanceThisYear: number;
  linkedActualThisYear: number;
  endsYear: number | null;
  unlinkedCandidateCount: number;
  unlinkedCandidateTotal: number;
};

export type BudgetCategoryGroupItem = {
  displayId: string;
  budgetCategoryId: number;
  categoryName: string;
  budgeted: number;
  actual: number;
  suggestedBudget: number;
  ratio: number;
  overBudget: boolean;
  isAnnual?: boolean;
  statusColor?: string;
  monthProgress?: number;
  isDerived?: boolean;
  isEditable?: boolean;
  derivedSourceLabel?: string | null;
  /** Temporary life events covering this category this year. */
  lifeEvents?: BudgetCategoryLifeEvent[];
};

type BudgetCategoryGroupsProps = {
  groups: CategoryGroup<BudgetCategoryGroupItem>[];
  expandedGroupId: string | null;
  onToggleGroup: (groupId: string) => void;
  editingCategoryId: string | null;
  getDraftValue: (categoryId: string) => string;
  onDraftChange: (categoryId: string, text: string) => void;
  onSave: (categoryId: string) => void;
  onStartEditing: (categoryId: string, currentAmount: number) => void;
  onCancelEditing: () => void;
  savingCategoryId: string | null;
  getRowErrorMessage?: (categoryId: string) => string | null;
  isReadOnly?: boolean;
  showTransactionLinks?: boolean;
  /** Bulk-link this year's unlinked category transactions to a life event. */
  onBulkLinkToEvent?: (goalId: string) => void;
  bulkLinkingGoalId?: string | null;
};

export function BudgetCategoryGroups({
  groups,
  expandedGroupId,
  onToggleGroup,
  editingCategoryId,
  getDraftValue,
  onDraftChange,
  onSave,
  onStartEditing,
  onCancelEditing,
  savingCategoryId,
  getRowErrorMessage,
  isReadOnly = false,
  showTransactionLinks = true,
  onBulkLinkToEvent,
  bulkLinkingGoalId = null,
}: BudgetCategoryGroupsProps) {
  const hasActiveEditor = editingCategoryId !== null;
  const router = useRouter();
  const c = useColors();

  return groups.map((group, groupIndex) => {
    const isExpanded = expandedGroupId === group.plaidPrimary;
    const totalBudgeted = group.categories.reduce((s, cat) => s + cat.budgeted, 0);
    const totalActual = group.categories.reduce((s, cat) => s + cat.actual, 0);
    const groupRatio = totalBudgeted > 0 ? totalActual / totalBudgeted : 0;
    const isOverBudget = totalBudgeted > 0 && totalActual > totalBudgeted;
    
    // For individual category progress bars, we use their specific month/year progress.
    // For the group summary, we use the first category's progress as a proxy (they should all be the same anyway).
    const groupMonthProgress = group.categories[0]?.monthProgress;

    return (
      <View key={group.plaidPrimary} className={groupIndex > 0 ? 'mt-3' : ''}>
        <Card>
          <Pressable 
            testID={`budget-group-toggle-${groupIndex}`}
            accessibilityLabel={`Toggle ${group.label} budget group`}
            onPress={() => onToggleGroup(group.plaidPrimary)}
            className="active:opacity-60"
          >
            <SectionHeader
              title={group.label}
              action={
                <View className="flex-row items-center gap-3">
                  {showTransactionLinks && (
                    <Pressable
                      onPress={(e) => {
                        e.stopPropagation();
                        router.push({
                          pathname: '/(app)/transactions',
                          params: { plaidPrimary: group.plaidPrimary, categoryLabel: group.label },
                        });
                      }}
                      className="flex-row items-center gap-1"
                    >
                      <Ionicons name="receipt-outline" size={13} color={c.primary} />
                      <Text className="text-xs font-medium text-green-600">View</Text>
                    </Pressable>
                  )}
                  <Ionicons 
                    name={isExpanded ? "chevron-up" : "chevron-down"} 
                    size={16} 
                    color={c.textSecondary} 
                  />
                </View>
              }
            />

            {!isExpanded && (
              <View className="pb-1">
                <View className="flex-row items-center justify-between mb-2">
                  <Money className="text-xs text-gray-500 dark:text-gray-400">
                    {formatCurrency(totalActual)} / {formatCurrency(totalBudgeted)}
                  </Money>
                  <Money
                    weight={500}
                    className={`text-xs ${
                      isOverBudget ? 'text-red-600 dark:text-red-400' : 'text-forest-600 dark:text-green-500'
                    }`}
                  >
                    {formatPercent(groupRatio)}
                  </Money>
                </View>
                <ProgressBar
                  value={groupRatio}
                  color={isOverBudget ? 'bg-red-400' : 'bg-green-400'}
                  marker={groupMonthProgress}
                  height={4}
                />
              </View>
            )}
          </Pressable>

          {isExpanded && group.categories.map((category, index) => {
            const isLast = index === group.categories.length - 1;
            const categoryId = category.displayId;
            const isEditing = editingCategoryId === categoryId;
            const rowErrorMessage = getRowErrorMessage?.(categoryId) ?? null;
            const rowTestId = `budget-category-row-${groupIndex}-${index}`;
            const rowAccessibilityLabel = `Edit ${category.categoryName} budget. ${
              category.budgeted > 0
                ? `Current budget ${formatCurrency(category.budgeted)}.`
                : 'No current budget.'
            } Actual ${category.isAnnual ? 'this year' : 'this month'} ${formatCurrency(category.actual)}.`;

            return (
              <View
                key={categoryId}
                className={`py-3 ${isLast ? '' : 'border-b border-gray-50 dark:border-gray-700'}`}
              >
                {isEditing ? (
                  <View
                    className="gap-3"
                    onStartShouldSetResponder={() => true}
                  >
                    <View className="flex-row justify-between items-start">
                      <View className="flex-1 mr-3">
                        <Text
                          className="text-sm font-medium text-gray-800 dark:text-gray-200"
                          numberOfLines={1}
                        >
                          {category.categoryName}
                        </Text>
                        {category.isDerived && (
                          <Text className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Derived from liability
                          </Text>
                        )}
                        <Text
                          className="mt-1 text-xs text-gray-500 dark:text-gray-400"
                          {...webPrivacyProps('financial')}
                        >
                          Actual this {category.isAnnual ? 'year' : 'month'} {formatCurrency(category.actual)}
                          {category.budgeted > 0 ? ` • Current budget ${formatCurrency(category.budgeted)}` : ''}
                        </Text>
                      </View>
                      {category.budgeted > 0 && (
                        <View
                          className={`rounded-full px-2 py-1 ${
                            category.overBudget
                              ? 'bg-red-50 dark:bg-red-900/30'
                              : 'bg-green-50 dark:bg-green-900/30'
                          }`}
                        >
                          <Text
                            className={`text-xs font-medium ${
                              category.overBudget
                                ? 'text-red-600 dark:text-red-400'
                                : 'text-green-600 dark:text-green-400'
                            }`}
                          >
                            {category.overBudget ? 'Over budget' : 'On track'}
                          </Text>
                        </View>
                      )}
                    </View>
                    <CurrencyInput
                      testID={`${rowTestId}-input`}
                      accessibilityLabel={`${category.categoryName} budget amount`}
                      label={category.isAnnual ? "Annual budget" : "Monthly budget"}
                      value={getDraftValue(categoryId)}
                      onChangeText={(text) => onDraftChange(categoryId, text)}
                      placeholder="0.00"
                      size="sm"
                      error={rowErrorMessage}
                      autoFocus
                    />
                    <View className="flex-row gap-2">
                      <Button
                        testID={`${rowTestId}-save-button`}
                        accessibilityLabel={`Save ${category.categoryName} budget`}
                        size="sm"
                        className="flex-1"
                        onPress={() => onSave(categoryId)}
                        loading={savingCategoryId === categoryId}
                        disabled={savingCategoryId !== null && savingCategoryId !== categoryId}
                      >
                        Save
                      </Button>
                      <Button
                        testID={`${rowTestId}-cancel-button`}
                        accessibilityLabel={`Cancel ${category.categoryName} budget edit`}
                        size="sm"
                        variant="secondary"
                        className="flex-1"
                        onPress={onCancelEditing}
                        disabled={savingCategoryId === categoryId}
                      >
                        Cancel
                      </Button>
                    </View>
                  </View>
                ) : (
                  <Pressable
                    testID={rowTestId}
                    accessibilityLabel={rowAccessibilityLabel}
                    onPress={() =>
                      onStartEditing(
                        categoryId,
                        category.budgeted > 0 ? category.budgeted : category.suggestedBudget,
                      )
                    }
                    disabled={
                      isReadOnly ||
                      category.isEditable === false ||
                      savingCategoryId !== null ||
                      hasActiveEditor
                    }
                  >
                    <View className="flex-row items-center justify-between">
                      <Text
                        className="text-sm font-medium text-gray-800 dark:text-gray-200 flex-1 mr-2"
                        numberOfLines={1}
                      >
                        {category.categoryName}
                      </Text>
                      <View className="flex-row items-center gap-3">
                        {category.isDerived && (
                          <Text className="text-[11px] text-gray-400 dark:text-gray-500">
                            Derived
                          </Text>
                        )}
                        {category.budgeted > 0 ? (
                          <Money className="text-xs text-gray-500 dark:text-gray-400">
                            {formatCurrency(category.budgeted)}
                          </Money>
                        ) : (
                          <Text className="text-xs text-gray-400 dark:text-gray-500">—</Text>
                        )}
                        <Money
                          weight={500}
                          className={`text-sm min-w-[60px] text-right ${
                            category.overBudget
                              ? 'text-red-600 dark:text-red-400'
                              : 'text-gray-800 dark:text-gray-200'
                          }`}
                        >
                          {formatCurrency(category.actual)}
                        </Money>
                      </View>
                    </View>
                    {category.budgeted > 0 && (
                      <View className="mt-2 flex-row items-center gap-2">
                        <View className="flex-1">
                          <ProgressBar
                            value={category.ratio}
                            color={category.statusColor ?? (category.overBudget ? 'bg-red-400' : 'bg-green-400')}
                            marker={category.monthProgress}
                            height={4}
                          />
                        </View>
                        <Text
                          className={`text-xs font-medium min-w-[45px] text-right ${
                            category.statusColor === 'bg-red-400'
                              ? 'text-red-600 dark:text-red-400'
                              : category.statusColor === 'bg-amber-400'
                              ? 'text-amber-600 dark:text-amber-400'
                              : 'text-green-600 dark:text-green-400'
                          }`}
                        >
                          {formatPercent(category.ratio)}
                        </Text>
                      </View>
                    )}
                  </Pressable>
                )}

                {!isEditing &&
                  (category.lifeEvents ?? []).map((event) => {
                    const eventRatio =
                      event.allowanceThisYear > 0
                        ? event.linkedActualThisYear / event.allowanceThisYear
                        : 0;
                    return (
                      <View
                        key={event.goalId}
                        className="mt-2 rounded-xl bg-amber-50 dark:bg-amber-900/20 px-3 py-2"
                      >
                        <View className="flex-row items-center justify-between">
                          <Text
                            className="text-xs font-medium text-amber-700 dark:text-amber-400 flex-1 mr-2"
                            numberOfLines={1}
                            {...webPrivacyProps('private')}
                          >
                            ⏳ {event.description} — life event
                          </Text>
                          <Money className="text-xs text-amber-700 dark:text-amber-400">
                            {formatCurrency(event.linkedActualThisYear)} /{' '}
                            {formatCurrency(event.allowanceThisYear)}
                          </Money>
                        </View>
                        <View className="mt-1.5">
                          <ProgressBar
                            value={eventRatio}
                            color="bg-amber-400"
                            height={4}
                          />
                        </View>
                        <View className="mt-1 flex-row items-center justify-between">
                          <Text className="text-[11px] text-amber-600/80 dark:text-amber-500/80">
                            Temporary{event.endsYear != null ? ` — ends ${event.endsYear}` : ''}
                          </Text>
                          {onBulkLinkToEvent && event.unlinkedCandidateCount > 0 && (
                            <Button
                              size="sm"
                              variant="ghost"
                              accessibilityLabel={`Link ${event.unlinkedCandidateCount} transactions to ${event.description}`}
                              onPress={() => onBulkLinkToEvent(event.goalId)}
                              loading={bulkLinkingGoalId === event.goalId}
                              disabled={bulkLinkingGoalId !== null}
                            >
                              {`Link ${event.unlinkedCandidateCount} transaction${
                                event.unlinkedCandidateCount === 1 ? '' : 's'
                              } (${formatCurrency(event.unlinkedCandidateTotal)})`}
                            </Button>
                          )}
                        </View>
                      </View>
                    );
                  })}
              </View>
            );
          })}
        </Card>
      </View>
    );
  });
}
